<!-- src/components/new-tip/ObjectivesGainsTable.vue -->
<template>
  <div>
    <v-card flat color="grey lighten-5" class="mb-3" v-if="!readonly">
      <v-card-text class="py-2 d-flex align-center flex-wrap" style="gap: 8px">
        <v-btn small outlined @click="dialogDefaults = true">
          <v-icon left small>mdi-tune</v-icon>
          Appliquer en masse
        </v-btn>
        <v-btn small outlined @click="addRow" color="primary">
          <v-icon left small>mdi-plus</v-icon>
          Ajouter un POS
        </v-btn>
        <v-spacer />
        <span class="text-caption grey--text">
          {{ rows.length }} affectation{{ rows.length > 1 ? 's' : '' }}
        </span>
      </v-card-text>
    </v-card>

    <v-data-table
      :headers="tableHeaders"
      :items="rows"
      :items-per-page="50"
      dense
      class="elevation-0 obj-table"
      :no-data-text="readonly ? 'Aucune affectation' : 'Cliquez sur Ajouter un POS pour commencer'"
    >
      <!-- POS : autocomplete débouncée sur le code -->
      <template #item.salepoint_id="{ item, index }">
        <v-autocomplete
          :value="item.salepoint_id"
          :items="getSalepointItemsFor(index)"
          :search-input.sync="searchInputs[index]"
          :loading="searchLoadings[index]"
          :readonly="readonly || (!!item.id)"
          :disabled="readonly"
          item-text="display_name"
          item-value="id"
          dense hide-details no-filter
          placeholder="Code POS..."
          :no-data-text="searchInputs[index] ? 'Aucun résultat' : 'Tapez un code POS'"
          @update:search-input="onSalepointSearch(index, $event)"
          @input="onSalepointInput(index, $event)"
          clearable
        />
      </template>

      <template #item.supervisor_id="{ item }">
        <v-autocomplete
          v-model="item.supervisor_id"
          :items="supervisorsOptions"
          item-text="display_name"
          item-value="id"
          dense hide-details
          :readonly="readonly"
          placeholder="Superviseur"
        />
      </template>

      <template #item.gob_objective_qty="{ item }">
        <v-text-field v-model.number="item.gob_objective_qty" type="number" min="0" dense hide-details :readonly="readonly" />
      </template>
      <template #item.west_objective_qty="{ item }">
        <v-text-field v-model.number="item.west_objective_qty" type="number" min="0" dense hide-details :readonly="readonly" />
      </template>
      <template #item.gub_objective_qty="{ item }">
        <v-text-field v-model.number="item.gub_objective_qty" type="number" min="0" dense hide-details :readonly="readonly" />
      </template>
      <template #item.hats_objective_qty="{ item }">
        <v-text-field v-model.number="item.hats_objective_qty" type="number" min="0" dense hide-details :readonly="readonly" />
      </template>
      <template #item.stock_bonus_percentage="{ item }">
        <v-text-field v-model.number="item.stock_bonus_percentage" type="number" min="0" max="100" suffix="%" dense hide-details :readonly="readonly" />
      </template>
      <template #item.gob_gain="{ item }">
        <v-text-field v-model.number="item.gob_gain" type="number" min="0" dense hide-details :readonly="readonly" />
      </template>
      <template #item.west_gain="{ item }">
        <v-text-field v-model.number="item.west_gain" type="number" min="0" dense hide-details :readonly="readonly" />
      </template>
      <template #item.gub_gain="{ item }">
        <v-text-field v-model.number="item.gub_gain" type="number" min="0" dense hide-details :readonly="readonly" />
      </template>
      <template #item.hats_gain="{ item }">
        <v-text-field v-model.number="item.hats_gain" type="number" min="0" dense hide-details :readonly="readonly" />
      </template>

      <template #item.actions="{ item, index }">
        <v-tooltip bottom v-if="!readonly && (item.visits_count || 0) === 0">
          <template #activator="{ on }">
            <v-btn icon small v-on="on" @click="removeRow(index, item)">
              <v-icon small color="error">mdi-delete</v-icon>
            </v-btn>
          </template>
          <span>Supprimer</span>
        </v-tooltip>
        <v-tooltip bottom v-else-if="(item.visits_count || 0) > 0">
          <template #activator="{ on }">
            <v-icon small color="grey" v-on="on">mdi-lock</v-icon>
          </template>
          <span>Affectation déjà visitée — non modifiable</span>
        </v-tooltip>
      </template>
    </v-data-table>

    <v-dialog v-model="dialogDefaults" max-width="500">
      <v-card>
        <v-card-title>Appliquer en masse</v-card-title>
        <v-card-text>
          <p class="text-body-2 grey--text">
            Les valeurs renseignées écraseront les valeurs actuelles de toutes
            les affectations non-encore-visitées. Les champs vides sont ignorés.
          </p>
          <v-row dense>
            <v-col cols="6"><v-text-field label="Obj GOB"  v-model.number="bulk.gob_objective_qty"  type="number" min="0" /></v-col>
            <v-col cols="6"><v-text-field label="Obj West" v-model.number="bulk.west_objective_qty" type="number" min="0" /></v-col>
            <v-col cols="6"><v-text-field label="Obj GUB"  v-model.number="bulk.gub_objective_qty"  type="number" min="0" /></v-col>
            <v-col cols="6"><v-text-field label="Obj Chap." v-model.number="bulk.hats_objective_qty" type="number" min="0" /></v-col>
            <v-col cols="6"><v-text-field label="Bonus %"  v-model.number="bulk.stock_bonus_percentage" type="number" min="0" max="100" suffix="%" /></v-col>
            <v-col cols="6"><v-text-field label="Gain GOB" v-model.number="bulk.gob_gain"  type="number" min="0" /></v-col>
            <v-col cols="6"><v-text-field label="Gain West" v-model.number="bulk.west_gain" type="number" min="0" /></v-col>
            <v-col cols="6"><v-text-field label="Gain GUB"  v-model.number="bulk.gub_gain" type="number" min="0" /></v-col>
            <v-col cols="6"><v-text-field label="Gain Chap." v-model.number="bulk.hats_gain" type="number" min="0" /></v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialogDefaults = false">Annuler</v-btn>
          <v-btn color="primary" @click="applyBulk">Appliquer</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { DEFAULT_VALUES } from '@/constants/newTip';
import api from '@/api/newTipApi';

export default {
  name: 'NewTipObjectivesGainsTable',

  props: {
    value: { type: Array, default: () => [] },
    supervisors: { type: Array, default: () => [] },
    knownSalepoints: { type: Array, default: () => [] },
    readonly: { type: Boolean, default: false },
  },

  data() {
    return {
      rows: this.value.map(r => ({ ...r })),
      dialogDefaults: false,
      bulk: {},
      // Cache global des POS rencontrés. Permet à v-autocomplete de toujours
      // retrouver le label d'un id sélectionné, même après perte de focus.
      // IMPORTANT : doit être réactif → on utilise this.$set pour ajouter
      // de nouvelles entrées.
      salepointsCache: {},
      searchInputs: [],
      searchResults: [],
      searchLoadings: [],
      searchTimers: [],
      tableHeaders: [
        { text: 'POS', value: 'salepoint_id', sortable: false, width: 260 },
        { text: 'Superviseur', value: 'supervisor_id', sortable: false, width: 200 },
        { text: 'Obj GOB', value: 'gob_objective_qty', sortable: false, width: 90 },
        { text: 'Obj West', value: 'west_objective_qty', sortable: false, width: 90 },
        { text: 'Obj GUB', value: 'gub_objective_qty', sortable: false, width: 90 },
        { text: 'Obj Chap.', value: 'hats_objective_qty', sortable: false, width: 90 },
        { text: 'Bonus%', value: 'stock_bonus_percentage', sortable: false, width: 90 },
        { text: 'Gain GOB', value: 'gob_gain', sortable: false, width: 90 },
        { text: 'Gain West', value: 'west_gain', sortable: false, width: 90 },
        { text: 'Gain GUB', value: 'gub_gain', sortable: false, width: 90 },
        { text: 'Gain Chap.', value: 'hats_gain', sortable: false, width: 90 },
        { text: '', value: 'actions', sortable: false, width: 50 },
      ],
    };
  },

  computed: {
    supervisorsOptions() {
      return this.supervisors.map(s => ({
        ...s,
        display_name: `${s.name} · ${s.phone}`,
      }));
    },
  },

  watch: {
    value: {
      deep: true,
      handler(v) {
        if (JSON.stringify(v) !== JSON.stringify(this.rows)) {
          this.rows = v.map(r => ({ ...r }));
          this.syncRowState();
        }
      },
    },
    rows: {
      deep: true,
      handler(v) { this.$emit('input', v); },
    },
    knownSalepoints: {
      immediate: true,
      handler(list) {
        if (!Array.isArray(list)) return;
        list.forEach(sp => {
          if (sp && sp.id) {
            this.$set(this.salepointsCache, sp.id, this.formatSalepoint(sp));
          }
        });
      },
    },
  },

  mounted() {
    this.syncRowState();
  },

  methods: {
    syncRowState() {
      while (this.searchInputs.length < this.rows.length) this.searchInputs.push('');
      while (this.searchResults.length < this.rows.length) this.searchResults.push([]);
      while (this.searchLoadings.length < this.rows.length) this.searchLoadings.push(false);
      while (this.searchTimers.length < this.rows.length) this.searchTimers.push(null);
    },

    formatSalepoint(sp) {
      return {
        id: sp.id,
        code: sp.code,
        name: sp.name,
        display_name: `${sp.code || '?'} · ${sp.name || ''}`,
      };
    },

    /**
     * Items affichés dans le dropdown du v-autocomplete d'une ligne donnée.
     *
     * RÈGLE CRITIQUE : la valeur actuellement sélectionnée DOIT toujours
     * être présente dans la liste, sinon Vuetify n'arrive pas à afficher
     * son label après perte de focus → bug "le POS se réinitialise".
     */
    getSalepointItemsFor(index) {
      const items = [];
      const seen = new Set();

      // 1) Toujours inclure le POS sélectionné s'il existe dans le cache
      const currentId = this.rows[index] && this.rows[index].salepoint_id;
      if (currentId && this.salepointsCache[currentId]) {
        items.push(this.salepointsCache[currentId]);
        seen.add(currentId);
      }

      // 2) Ajouter les résultats de la recherche en cours
      const results = this.searchResults[index] || [];
      results.forEach(sp => {
        if (!seen.has(sp.id)) {
          items.push(sp);
          seen.add(sp.id);
        }
      });

      return items;
    },

    onSalepointSearch(index, value) {
      const keyword = value || '';

      // Si le texte tapé correspond au label de l'item sélectionné, ne pas
      // relancer la recherche (cas où l'utilisateur clique dans le champ
      // après sélection)
      const currentId = this.rows[index] && this.rows[index].salepoint_id;
      const currentLabel = currentId && this.salepointsCache[currentId]
        ? this.salepointsCache[currentId].display_name
        : null;
      if (keyword === currentLabel) return;

      // Ne pas chercher si la valeur sélectionnée existe et que le keyword est vide
      // (cas de la perte de focus qui vide search-input automatiquement)
      if (!keyword && currentId) return;

      if (this.searchTimers[index]) {
        clearTimeout(this.searchTimers[index]);
      }
      this.$set(this.searchTimers, index, setTimeout(() => {
        this.fetchSalepoints(index, keyword);
      }, 300));
    },

    async fetchSalepoints(index, keyword) {
      if (!keyword || keyword.length < 1) {
        this.$set(this.searchResults, index, []);
        return;
      }
      this.$set(this.searchLoadings, index, true);
      try {
        const { data } = await api.searchSalepoints(keyword, 10);
        const list = (data && data.data && data.data.data) || data.data || [];
        const formatted = list.map(sp => this.formatSalepoint(sp));
        // Ajouter au cache de manière réactive
        formatted.forEach(sp => { this.$set(this.salepointsCache, sp.id, sp); });
        this.$set(this.searchResults, index, formatted);
      } catch (err) {
        console.error('searchSalepoints failed', err);
        this.$set(this.searchResults, index, []);
      } finally {
        this.$set(this.searchLoadings, index, false);
      }
    },

    /**
     * Appelé quand l'utilisateur sélectionne un POS dans le dropdown
     * (event @input du v-autocomplete).
     */
    onSalepointInput(index, newId) {
      // Mettre à jour la ligne (puisqu'on n'utilise pas v-model directement
      // mais :value + @input pour mieux contrôler le cycle)
      if (!this.rows[index]) return;
      this.$set(this.rows[index], 'salepoint_id', newId);

      // Si l'utilisateur a vidé le champ (clearable), reset le search-input
      if (newId === null || newId === undefined) {
        this.$set(this.searchInputs, index, '');
        this.$set(this.searchResults, index, []);
      }
    },

    addRow() {
      this.rows.push({
        salepoint_id: null,
        supervisor_id: null,
        gob_objective_qty: null,
        west_objective_qty: null,
        gub_objective_qty: null,
        hats_objective_qty: DEFAULT_VALUES.hats_objective_qty,
        stock_bonus_percentage: DEFAULT_VALUES.stock_bonus_percentage,
        gob_gain: DEFAULT_VALUES.gob_gain,
        west_gain: DEFAULT_VALUES.west_gain,
        gub_gain: DEFAULT_VALUES.gub_gain,
        hats_gain: DEFAULT_VALUES.hats_gain,
        visits_count: 0,
      });
      this.syncRowState();
    },

    removeRow(index, item) {
      if (item.id) this.$emit('row-delete', item.id);
      this.rows.splice(index, 1);
      this.searchInputs.splice(index, 1);
      this.searchResults.splice(index, 1);
      this.searchLoadings.splice(index, 1);
      this.searchTimers.splice(index, 1);
    },

    applyBulk() {
      this.rows.forEach(r => {
        if ((r.visits_count || 0) > 0) return;
        Object.entries(this.bulk).forEach(([k, v]) => {
          if (v !== null && v !== undefined && v !== '') {
            r[k] = v;
          }
        });
      });
      this.dialogDefaults = false;
      this.bulk = {};
    },
  },
};
</script>

<style scoped>
.obj-table >>> td { vertical-align: middle; }
.obj-table >>> .v-text-field { padding-top: 0; margin-top: 0; }
</style>
